if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.skipPersonalizationValidation = false;
        this.skipAttachmentValidation = false;
        this.pendingDynamicCheckout = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Set up personalization validation modal handlers
        this.setupPersonalizationValidationModal();
        
        // Set up attachment validation modal handlers
        this.setupAttachmentValidationModal();
        
        // Set up dynamic checkout button validation
        this.setupDynamicCheckoutValidation();
      }

      /**
       * Set up event listeners for the personalization validation modal
       */
      setupPersonalizationValidationModal() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const modal = document.getElementById(`PersonalizationValidationModal-${sectionId}`);
        if (!modal) return;

        // Handle "Add personalization" button
        const addPersonalizationBtn = modal.querySelector('[data-action="add-personalization"]');
        if (addPersonalizationBtn) {
          addPersonalizationBtn.addEventListener('click', () => {
            modal.hide();
            this.pendingDynamicCheckout = false;
            this.scrollToPersonalizationSection();
          });
        }

        // Handle "Continue without" button
        const continueWithoutBtn = modal.querySelector('[data-action="continue-without"]');
        if (continueWithoutBtn) {
          continueWithoutBtn.addEventListener('click', () => {
            modal.hide();
            this.skipPersonalizationValidation = true;
            
            // Check if we should trigger dynamic checkout or regular add to cart
            if (this.pendingDynamicCheckout) {
              this.pendingDynamicCheckout = false;
              this.triggerDynamicCheckout();
            } else {
              // Re-submit the form for regular add to cart
              this.form.requestSubmit();
            }
          });
        }
      }

      /**
       * Set up event listeners for the attachment validation modal
       */
      setupAttachmentValidationModal() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const modal = document.getElementById(`AttachmentValidationModal-${sectionId}`);
        if (!modal) return;

        // Handle "Add attachment" button
        const addAttachmentBtn = modal.querySelector('[data-action="add-attachment"]');
        if (addAttachmentBtn) {
          addAttachmentBtn.addEventListener('click', () => {
            modal.hide();
            this.pendingDynamicCheckout = false;
            this.scrollToAttachmentSection();
          });
        }

        // Handle "Continue without" button
        const continueWithoutBtn = modal.querySelector('[data-action="continue-without-attachment"]');
        if (continueWithoutBtn) {
          continueWithoutBtn.addEventListener('click', () => {
            modal.hide();
            this.skipAttachmentValidation = true;
            
            // Check if we should trigger dynamic checkout or regular add to cart
            if (this.pendingDynamicCheckout) {
              this.pendingDynamicCheckout = false;
              this.triggerDynamicCheckout();
            } else {
              // Re-submit the form for regular add to cart
              this.form.requestSubmit();
            }
          });
        }
      }

      /**
       * Set up validation for dynamic checkout button (Buy Now/Shop Pay)
       */
      setupDynamicCheckoutValidation() {
        // Wait for Shopify to render the payment button
        const observer = new MutationObserver((mutations, obs) => {
          const paymentButton = this.querySelector('.shopify-payment-button__button');
          if (paymentButton) {
            this.interceptDynamicCheckoutButton(paymentButton);
            obs.disconnect();
          }
        });

        // Start observing for the payment button
        observer.observe(this, { childList: true, subtree: true });

        // Also check if it already exists
        const existingButton = this.querySelector('.shopify-payment-button__button');
        if (existingButton) {
          this.interceptDynamicCheckoutButton(existingButton);
          observer.disconnect();
        }
      }

      /**
       * Intercept clicks on the dynamic checkout button
       */
      interceptDynamicCheckoutButton(button) {
        // Add a capturing event listener to intercept before Shopify handles it
        button.addEventListener('click', (e) => {
          // Check if personalization validation is needed
          if (!this.skipPersonalizationValidation && this.needsPersonalizationValidation()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.pendingDynamicCheckout = true;
            this.showPersonalizationValidationModal();
            return false;
          }
          // Reset the skip flag
          this.skipPersonalizationValidation = false;
          
          // Check if attachment validation is needed
          if (!this.skipAttachmentValidation && this.needsAttachmentValidation()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.pendingDynamicCheckout = true;
            this.showAttachmentValidationModal();
            return false;
          }
          // Reset the skip flag
          this.skipAttachmentValidation = false;
        }, true); // Use capture phase
      }

      /**
       * Trigger the dynamic checkout button click programmatically
       */
      triggerDynamicCheckout() {
        const paymentButton = this.querySelector('.shopify-payment-button__button');
        if (paymentButton) {
          // Skip validation for this click
          this.skipPersonalizationValidation = true;
          this.skipAttachmentValidation = true;
          // Trigger a new click event
          paymentButton.click();
        }
      }

      /**
       * Scroll to the personalization section and open the accordion
       */
      scrollToPersonalizationSection() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const customizedProductModel = document.getElementById(`CustomizedProductModel-${sectionId}`);
        if (!customizedProductModel) return;

        // Open the accordion if it's closed
        const details = customizedProductModel.querySelector('details');
        if (details && !details.open) {
          details.open = true;
        }

        // Scroll to the element
        customizedProductModel.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Focus the first input field
        setTimeout(() => {
          const firstInput = customizedProductModel.querySelector('input[data-personalization-field="line1"]');
          if (firstInput) {
            firstInput.focus();
          }
        }, 500);
      }

      /**
       * Check if personalization validation is required
       * Returns true if a customizable variant is selected but no personalization data is entered
       */
      needsPersonalizationValidation() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return false;

        const customizedProductModel = document.getElementById(`CustomizedProductModel-${sectionId}`);
        if (!customizedProductModel) return false;

        // Check if the accordion container is visible (means a customizable variant is selected)
        const accordionContainer = customizedProductModel.querySelector('.customized-product-model__accordion');
        if (!accordionContainer || accordionContainer.style.display === 'none') {
          return false;
        }

        // Check if line1 and line2 inputs are empty
        const line1Input = customizedProductModel.querySelector('input[data-personalization-field="line1"]');
        const line2Input = customizedProductModel.querySelector('input[data-personalization-field="line2"]');

        const line1Value = line1Input?.value?.trim() || '';
        const line2Value = line2Input?.value?.trim() || '';

        // If both lines are empty, validation is needed
        return line1Value === '' && line2Value === '';
      }

      /**
       * Show the personalization validation modal
       */
      showPersonalizationValidationModal() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const modal = document.getElementById(`PersonalizationValidationModal-${sectionId}`);
        if (modal && typeof modal.show === 'function') {
          modal.show(this.submitButton);
        }
      }

      /**
       * Scroll to the attachment section
       */
      scrollToAttachmentSection() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const attachmentInput = document.querySelector(`file-attachment-input[data-section-id="${sectionId}"]`);
        if (!attachmentInput) return;

        // Scroll to the element
        attachmentInput.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Focus the textarea
        setTimeout(() => {
          const textarea = attachmentInput.querySelector('[data-attachment-description]');
          if (textarea) {
            textarea.focus();
          }
        }, 500);
      }

      /**
       * Check if attachment validation is required
       * Returns true if a variant with piece_jointe is selected but no file or description is provided
       */
      needsAttachmentValidation() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return false;

        const attachmentInput = document.querySelector(`file-attachment-input[data-section-id="${sectionId}"]`);
        if (!attachmentInput) return false;

        // Check if the attachment input is visible (means a variant with piece_jointe is selected)
        if (attachmentInput.classList.contains('file-attachment-input--hidden')) {
          return false;
        }

        // Check if file is selected
        const fileInput = attachmentInput.querySelector('[data-file-input]');
        const hasFile = fileInput?.files?.length > 0;

        // Check if description is provided
        const textarea = attachmentInput.querySelector('[data-attachment-description]');
        const hasDescription = textarea?.value?.trim() !== '';

        // Validation is needed if neither file nor description is provided
        return !hasFile && !hasDescription;
      }

      /**
       * Show the attachment validation modal
       */
      showAttachmentValidationModal() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;

        const modal = document.getElementById(`AttachmentValidationModal-${sectionId}`);
        if (modal && typeof modal.show === 'function') {
          modal.show(this.submitButton);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check for personalization validation (unless skipped)
        if (!this.skipPersonalizationValidation && this.needsPersonalizationValidation()) {
          this.showPersonalizationValidationModal();
          return;
        }

        // Reset the skip flag for future submissions
        this.skipPersonalizationValidation = false;

        // Check for attachment validation (unless skipped)
        if (!this.skipAttachmentValidation && this.needsAttachmentValidation()) {
          this.showAttachmentValidationModal();
          return;
        }

        // Reset the skip flag for future submissions
        this.skipAttachmentValidation = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
